import { FC, useState } from 'react';
import { CareMapDrawerState } from '../../caremap/CareMapDrawerState';
import { useSubscription } from '@meraki-internal/state';
import { CareMapDrawerPage } from '../../components/CareMapDrawerPage';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { NEW_MEDICATION_ID } from '../../caremap/ICareMap';
import { CareMapState } from '../../caremap/CareMapState';
import { MedicationEditor } from './MedicationEditor';
import { MedicationFormState } from './MedicationFormState';
import { Menu } from '../../components/Menu';
import { DeleteIcon } from '../../theme/Icons';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';

export type IMedicationEditorDrawerProps = {
    id: string;
};

export const MedicationEditorCareMapDrawer: FC<IMedicationEditorDrawerProps> = ({ id }) => {
    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);

    const drawerState = useInstance(CareMapDrawerState);
    useSubscription(() => drawerState);

    const [saving, setSaving] = useState(false);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const medicationForm = useInstance(MedicationFormState);
    useSubscription(() => medicationForm);

    const isNew = id === NEW_MEDICATION_ID;
    const medication = medicationForm.state.medication;

    const isViewer = !careMapState.hasPermissionTo('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const canAddEdit = !isViewer && !isAnonymous;

    const onDidEnter = () => {
        drawerState.setIsMaskingFocusedCareMap(true);

        if (!isNew) {
            mixpanel.track('Medication Viewed', () => ({
                'Name': medication?.name,
                'Has Instructions': medication?.instructions ? true : false,
                'Has Dose': medication?.dose ? true : false,
                'Has RX #': medication?.rxNumber ? true : false,
                'Has Refills': medication?.refills ? true : false,
                'Has Expiration Date': medication?.expiration ? true : false,
                'Has Notes': medication?.notes ? true : false
            }));
        }
    };

    const onWillLeave = () => {
        drawerState.setIsMaskingFocusedCareMap(false);
    };

    const [isValid, setIsValid] = useState<boolean>(false);

    if (!careMapState.getActiveCaremap()) {
        return null;
    }

    const deleteMedication = async () => {
        setSaving(true);
        medicationForm.delete();
        history.back();
    };

    const save = async () => {
        if (medication) {
            setSaving(true);
            medicationForm.updateCurrentPatient();
            history.back();
        }
    };

    const cancel = () => {
        medicationForm.clear();
        history.back();
    };

    return (
        <CareMapDrawerPage
            hasBack
            enteredHandler={onDidEnter}
            leavingHandler={onWillLeave}
            data-type="medication-editor"
            title={`${isNew ? 'Add' : 'Edit'} Medication`}
            buttons={
                canAddEdit && <>
                    <IonButton data-type="save-button" disabled={!isValid || saving} onClick={save}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={cancel}>Cancel</IonButton>
                </>
            }
        >
            {(canAddEdit && !isNew) ? (
                <div style={{marginTop: -8, display: 'flex', justifyContent: 'end'}}>
                    <Menu data-id="edit-medication-menu" actions={[{
                        Icon: DeleteIcon,
                        role: 'destructive',
                        text: `Delete Medication`,
                        handler: deleteMedication,
                        'data-type': 'delete-button',
                        disabled: saving
                    }]} />
                </div>
            ) : <div data-type="no-menu" />}

            <MedicationEditor
                id={id}
                readOnly={!canAddEdit}
                reportValidity={(valid) => setIsValid(valid)}
            />
        </CareMapDrawerPage>
    );
};
